
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

import { devError } from '@/utils/devLog';
interface FavoritesContextType {
  favorites: number[];
  loading: boolean;
  isFavorite: (productId: number) => boolean;
  toggleFavorite: (productId: number) => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | null>(null);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // Charger les favoris au démarrage si l'utilisateur est connecté
  useEffect(() => {
    const fetchFavorites = async () => {
      if (user) {
        setLoading(true);
        try {
          const ids = await api.getFavorites(user.id);
          setFavorites(ids);
        } catch (error) {
          devError("Failed to fetch favorites", error);
        } finally {
          setLoading(false);
        }
      } else {
        setFavorites([]);
      }
    };
    fetchFavorites();
  }, [user]);

  const isFavorite = useCallback((productId: number) => {
    return favorites.includes(productId);
  }, [favorites]);

  const toggleFavorite = async (productId: number) => {
    if (!user) return; // Ou rediriger vers login

    const isAlreadyFavorite = favorites.includes(productId);
    
    // Optimistic update
    setFavorites(prev => 
        isAlreadyFavorite ? prev.filter(id => id !== productId) : [...prev, productId]
    );

    try {
        if (isAlreadyFavorite) {
            await api.removeFavorite(user.id, productId);
        } else {
            await api.addFavorite(user.id, productId);
        }
    } catch (error) {
        devError("Failed to toggle favorite", error);
        // Revert optimistic update on error
        setFavorites(prev => 
            isAlreadyFavorite ? [...prev, productId] : prev.filter(id => id !== productId)
        );
    }
  };

  return (
    <FavoritesContext.Provider value={{ favorites, loading, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
